import { TicketTier } from '../types/events';
import { Timestamp } from 'firebase/firestore';

/**
 * Safely gets ticket tiers from an event, ensuring the result is always an array
 * @param event The event object that may contain ticketTiers
 * @returns An array of ticket tiers or an empty array if none exist
 */
export const getTicketTiersArray = (event: any): TicketTier[] => {
  if (!event) return [];
  
  // If ticketTiers is already an array, return it
  if (Array.isArray(event.ticketTiers)) {
    return event.ticketTiers;
  }
  
  // If ticketTiers is an object with numeric keys (like from Firestore), convert to array
  if (event.ticketTiers && typeof event.ticketTiers === 'object') {
    // Try to convert object to array if it has numeric keys
    const ticketTiersArray = Object.keys(event.ticketTiers)
      .filter(key => !isNaN(Number(key)))
      .map(key => event.ticketTiers[key]);
      
    if (ticketTiersArray.length > 0) {
      return ticketTiersArray;
    }
  }
  
  return [];
};

/**
 * Safely checks if an event has any available tickets
 * @param event The event object to check
 * @returns True if the event has any tickets with quantity > sold
 */
export const hasAvailableTickets = (event: any): boolean => {
  const ticketTiers = getTicketTiersArray(event);
  
  return ticketTiers.some(tier => 
    (tier.quantity || 0) - (tier.sold || 0) > 0
  );
};

/**
 * Gets the minimum ticket price from available ticket tiers
 * @param event The event object to check
 * @returns The minimum price or null if no tickets exist
 */
export const getMinTicketPrice = (event: any): number | null => {
  const ticketTiers = getTicketTiersArray(event);
  
  if (ticketTiers.length === 0) return null;
  
  return ticketTiers.reduce((min, tier) => 
    Math.min(min, tier.price || Infinity), Infinity
  ) || null;
};

/**
 * Checks if an event has already passed based on its end date
 * @param event The event object to check
 * @returns True if the event has ended, false otherwise or if dates are invalid
 */
export const isEventPast = (event: any): boolean => {
  if (!event || !event.endDateTime) return false;
  
  try {
    // Safely convert end date to a Date object
    const endDate = event.endDateTime instanceof Timestamp 
      ? event.endDateTime.toDate() 
      : new Date(event.endDateTime);
      
    // Check if valid date
    if (isNaN(endDate.getTime())) {
      console.error('Invalid end date in event:', event);
      return false;
    }
    
    const now = new Date();
    return now > endDate;
  } catch (error) {
    console.error('Error checking if event is past:', error);
    return false; // Default to not past on error
  }
}; 